/**
 * Single source of truth for "How to Play" content.
 * Consumed by:
 *   - HowToPlay.jsx (full page with all sections)
 *   - HowToPlayFlipCard.jsx (flip cards on Single Player + Multiplayer screens)
 */
import { IconClapper, IconDice, IconScroll, IconLightbulb, IconSwords, IconHandshake, IconBolt } from '../components/icons'

export const SINGLE_PLAYER_MODES = [
  {
    id: 'daily',
    Icon: IconClapper,
    name: 'Daily Challenge',
    cost: 'Free — 1 player',
    points: [
      'A new puzzle every day at midnight',
      "Object: Connect today's start actor to the goal actor in as few hops as possible",
      "A hop = picking a movie + picking an actor from that movie's cast",
      'The start actor never counts as a hop',
      'Free to play every day — earns 1 ticket on completion',
      'Build your daily streak to unlock Hollywood Career themes',
      "Yesterday's goal actor becomes today's start actor — one infinite chain!",
      'How to win: reach the goal actor in fewer hops than your previous best',
    ],
  },
  {
    id: 'single',
    Icon: IconDice,
    name: 'Single Player',
    cost: '1 ticket — 1 player',
    points: [
      'Pick any two actors using the search bar',
      'Object: Connect them using as few hops as possible',
      "Browse your start actor's movies → tap a movie → browse the cast → tap an actor → repeat",
      'Use Cue Cards for hints — costs 1 ticket, reveals a bridge actor with a temperature rating showing how close you are',
      'How to win: reach the goal actor. Fewer hops = better score.',
    ],
  },
  {
    id: 'historical',
    Icon: IconScroll,
    name: 'Historical Puzzles',
    cost: '1 ticket — 1 player',
    points: [
      'Replay any past Daily Challenge',
      'Object: Beat your previous best score on that puzzle',
      'Same rules as Daily Challenge',
      'How to win: reach the goal actor in fewer hops than your recorded best',
    ],
  },
]

export const SINGLE_PLAYER_TIPS = {
  Icon: IconLightbulb,
  name: 'Tips',
  points: [
    'Start with well-known actors — they appear in more movies',
    'Actors who span multiple genres make great bridges',
    'The goal actor glows gold in cast grids — keep your eye out!',
    'Cue Cards show a temperature: Freezing = far away, Hot = one hop away, Spoiler = that IS the goal actor',
  ],
}

export const MULTIPLAYER_MODES = [
  {
    id: 'h2h',
    Icon: IconSwords,
    name: 'Challenge a Friend — Head to Head',
    cost: '1 ticket per player — 2 players',
    points: [
      'One player creates a game and shares the code, the other joins',
      'Object: Connect the same start and goal actors in fewer hops than your opponent',
      'Both players get the exact same puzzle',
      'Play simultaneously or async — save and resume anytime',
      'How to win: fewest hops wins. Time breaks ties.',
      'If you quit after making 1+ hops, you lose your ticket. 0 hops = full refund.',
    ],
  },
  {
    id: 'coop',
    Icon: IconHandshake,
    name: 'Co-Op',
    cost: '1 ticket per player — 2 players',
    points: [
      'Team up with a friend using a game code',
      'Object: Solve the puzzle together as a team',
      'Take turns — each player picks a movie then an actor before passing the torch',
      'Both players see the shared chain building in real time',
      'How to win: reach the goal actor together. Collective hops + time = your team score. No winner — just teamwork!',
    ],
  },
]

export const PARTY_MODE_INTRO =
  'Party Mode requires a host screen — a laptop or TV. One person clicks Start Party and a popup window opens. ' +
  "That's your host/TV screen. Everyone else joins on their phones using the 6-character game code displayed on the host screen."

export const PARTY_MODES = [
  {
    id: 'battle-royale',
    Icon: IconSwords,
    name: 'Battle Royale',
    cost: '3–12 players',
    points: [
      'Every player races simultaneously on their own phone',
      'Object: Connect the start actor to the goal actor',
      'Two random players secretly pick the start and goal actors — revealed dramatically to everyone at once',
      'How to win: fewest hops wins. Time breaks ties. Every player for themselves.',
    ],
  },
  {
    id: 'speed-run',
    Icon: IconBolt,
    name: 'Speed Run',
    cost: '3–12 players',
    points: [
      'Every player races simultaneously',
      'Object: Be the first to reach the goal actor',
      'Same puzzle for everyone — pure race',
      "How to win: fastest time wins. Hops don't matter at all.",
    ],
  },
  {
    id: 'party-coop',
    Icon: IconHandshake,
    name: 'Co-Op',
    cost: '3–12 players',
    points: [
      'The whole room works as one team',
      'Object: Solve the puzzle together',
      'Take turns — each player picks a movie then an actor then passes the torch',
      'Stuck? After 2 minutes a Cameo option appears — the game makes the move for you',
      "How to win: there's no winner — collective hops + time = your team score. Everyone wins together.",
    ],
  },
  {
    id: 'h2h-teams',
    Icon: IconSwords,
    name: 'H2H Teams',
    cost: '4–12 players',
    points: [
      'Director builds teams using the tap-to-assign team builder',
      'Object: Your team connects the actors before the other team(s) do',
      'Teams race simultaneously — each team member takes turns picking for their team',
      'Two random players (ideally one per team) secretly pick the start and goal actors',
      'How to win: first team to reach the goal actor wins. Fewest hops takes it.',
    ],
  },
  {
    id: 'directors-cut',
    Icon: IconClapper,
    name: "Director's Cut",
    cost: '4–12 players',
    points: [
      'One player is the Director — they make every single decision',
      'Everyone else is an Agent — you nominate movies and actors each round',
      'Object: Score the most points by having your nominations chosen by the Director',
      'Each round: all agents nominate a movie → Director picks one anonymously → all agents nominate an actor → Director picks one → repeat',
      'Director can flip any card to see cast/filmography before deciding',
      'Scoring: your nominated actor chosen as start/goal = +2 points. Your nominated movie or actor chosen = +1 point. Duplicates both score!',
      'How to win: most points = Agent of the Year. Director breaks ties.',
    ],
  },
  {
    id: 'trilogy',
    Icon: IconClapper,
    name: 'The Trilogy',
    cost: '3–12 players',
    points: [
      'Three rounds, three puzzles — one champion takes it all',
      'Each round, two players are randomly chosen as puzzle setters — a coin flip decides which actor is the start and which is the goal',
      'Everyone races simultaneously on their own phone to connect the start actor to the goal actor in as few hops as possible',
      'After each round, cumulative standings are revealed before the next round begins',
      'Scoring: fewest total hops across all 3 rounds wins — equal hops? Lowest total time wins',
      'Scores carry over across all three rounds — consistency beats one spectacular solve',
      'Picker tip: pick a well-connected actor to make the puzzle easier — or an obscure one to trip everyone up',
      'Even if you lose a round, a fast clean solve keeps you in contention',
    ],
  },
]

export const PARTY_MODE_TIPS = {
  Icon: IconLightbulb,
  name: 'Party Mode Tips',
  points: [
    'The host screen is best on a TV or large monitor so everyone can see it. Use another device such as a laptop or tablet to host the party mode from.',
    'Make sure everyone joins before the host clicks "Everyone\'s In!"',
    'The start and goal actors are revealed dramatically to the whole room simultaneously',
    'Tickets are charged when you tap "Let\'s Go!" — leaving before that = no charge',
  ],
}
